import { chargeBeePlans, PlanIdToNameMap } from './plans';
import { BillingUser as User } from './user';
import { ChargeBeePlan, ChargeBeePlanId, PlanTier, UserPlans, BillingPeriod, CustomDealDetails } from './types';
import { resolvePlanForSubscription } from './pricing-resolver';

declare const process: { env: { NODE_ENV?: string; PRERENDER_ENVIRONMENT?: string } };

const FORCE_NEW_PRICING_PLANS_AVAILABLE = true;

export const isEnterprise = (planId: ChargeBeePlanId) =>
  planId === ChargeBeePlanId.Enterprise ||
  planId === ChargeBeePlanId.Enterprise2022 ||
  planId === ChargeBeePlanId.CustomEnterprise ||
  planId === ChargeBeePlanId.CustomEnterpriseYearly ||
  planId === ChargeBeePlanId.Custom2024 ||
  planId === ChargeBeePlanId.EnterprisePlus ||
  planId === ChargeBeePlanId.EnterprisePlusYearly ||
  planId === ChargeBeePlanId.CustomEnterprise3Years;

export const isFree = (planId: ChargeBeePlanId) => planId === ChargeBeePlanId.Basic;

export const isStarterPlanId = (planId: string): boolean =>
  planId === ChargeBeePlanId.Starter || planId === ChargeBeePlanId.StarterStaging;

export const isCustom = (planId: ChargeBeePlanId) =>
  planId === ChargeBeePlanId.CustomEnterprise ||
  planId === ChargeBeePlanId.CustomEnterpriseYearly ||
  planId === ChargeBeePlanId.EnterprisePlus ||
  planId === ChargeBeePlanId.EnterprisePlusYearly ||
  planId === ChargeBeePlanId.CustomEnterprise3Years;

export const getPlanById = (chargebeePlanId: string): ChargeBeePlan => {
  const plan = chargeBeePlans.find((p) => p.chargeBeePlanId === chargebeePlanId);

  if (!plan) throw new Error(`PlanId not found: ${chargebeePlanId}`);

  return plan;
};

/**
 * Returns data retention days for the plan when applicable (P2025 tier with dataRetentionDays set).
 * Returns undefined for unknown plan, non-P2025, or when retention is not set.
 */
export function getRetentionDaysForPlan(chargebeePlanId: string): number | undefined {
  try {
    const plan = getPlanById(chargebeePlanId);
    if (plan.tier === PlanTier.P2025 && typeof plan.dataRetentionDays === 'number' && plan.dataRetentionDays > 0) {
      return plan.dataRetentionDays;
    }
  } catch {
    // Unknown plan
  }
  return undefined;
}

/** 0 = not included. Unknown plan ids fail closed to 0. */
export function getSearchPerformanceMaxDomains(chargebeePlanId: string): number {
  try {
    return getPlanById(chargebeePlanId).searchPerformanceMaxDomains;
  } catch {
    return 0;
  }
}

/**
 * Max allowed domain scheduler pattern rules per domain.
 * Used for both creation limit and plan-change cleanup so they stay in sync.
 * If the user has more than 0 rules on his plan, return the value from the plan, otherwise return 5 if the feature flag is present. Default to 0.
 * TODO remove with new plans introduction (feature-flag fallback).
 */
export function getDomainSchedulerSettingsMaxRules(
  plan: ChargeBeePlan | undefined,
  availableFeatures?: string[]
): number {
  if (plan?.domainSchedulerSettingsMaxRules !== undefined && plan?.domainSchedulerSettingsMaxRules > 0) {
    return plan?.domainSchedulerSettingsMaxRules;
  }
  if (availableFeatures?.includes('domain-scheduler-settings')) {
    return 5;
  }
  return 0;
}

export function isPlanEqualOrHigher(userPlan: ChargeBeePlanId, minPlan: ChargeBeePlanId): boolean {
  const RANKED_2025_PLANS: Partial<Record<ChargeBeePlanId, number>> = {
    [ChargeBeePlanId.Starter]: 1,
    [ChargeBeePlanId.Growth]: 2,
    [ChargeBeePlanId.Pro]: 3,
    [ChargeBeePlanId.EnterprisePlus]: 4,
    [ChargeBeePlanId.EnterprisePlusYearly]: 4,
    [ChargeBeePlanId.CustomEnterprise3Years]: 4,
  };

  const userPlanIndex = RANKED_2025_PLANS[userPlan];
  if (userPlanIndex === undefined) return true;

  const minPlanIndex = RANKED_2025_PLANS[minPlan] || -1;

  return userPlanIndex >= minPlanIndex;
}

export const isPlanAvailable = (user: User, currentTier: PlanTier, plan: ChargeBeePlan) => {
  if (
    (plan.chargeBeePlanId === ChargeBeePlanId.StarterStaging ||
      plan.chargeBeePlanId === ChargeBeePlanId.GrowthStaging) &&
    process.env.PRERENDER_ENVIRONMENT === 'production'
  ) {
    return false;
  }

  if (
    plan.chargeBeePlanId === ChargeBeePlanId.EnterprisePlusYearly ||
    plan.chargeBeePlanId === ChargeBeePlanId.CustomEnterprise3Years
  ) {
    return false;
  }

  if (FORCE_NEW_PRICING_PLANS_AVAILABLE || !user.legacy2025) {
    // new 2025 pricing
    switch (currentTier) {
      case PlanTier.Custom:
        return false;
      default:
        return plan.tier === PlanTier.P2025;
    }
  }

  // todo remove after pricing2025 is live for everyone
  if (plan.chargeBeePlanId === ChargeBeePlanId.Lite2024) {
    return user.chargebee_plan_id === ChargeBeePlanId.Lite2024;
  }
  switch (currentTier) {
    case PlanTier.Free:
    case PlanTier.Legacy:
    case PlanTier.P2024:
      return plan.tier === PlanTier.Free || plan.tier === PlanTier.P2024;
    case PlanTier.P2020:
    case PlanTier.P2022:
      return plan.tier === PlanTier.Free || plan.tier === PlanTier.P2022;
    case PlanTier.Custom:
      return false;
    case PlanTier.P2025:
      return plan.tier === PlanTier.P2025;
  }
};

export const getHighestMeteredPlan = (chargebeePlanIds: Array<string>): ChargeBeePlan | undefined => {
  try {
    const meteredPlansInput = chargebeePlanIds.map(getPlanById);

    return chargeBeePlans
      .filter((plan) => !plan.legacy)
      .slice()
      .sort((rp, lp) => rp.index - lp.index)
      .reverse()
      .find((meteredPlan) => meteredPlansInput.includes(meteredPlan));
  } catch (error) {
    return undefined;
  }
};

const calculateAddonsCost = (user: User, plan: ChargeBeePlan): number => {
  if (plan.legacy) {
    return 0;
  }

  return (
    plan.addonCostInCents * Math.max(0, Math.ceil((user.cached_render_counter - plan.includedRenderedPages) / 1000))
  );
};

export const calculateCustomDealAddonsCost = (user: User, customDeal: CustomDealDetails): number => {
  return (
    customDeal.extraRendersPrice *
    Math.max(0, Math.ceil((user.cached_render_counter - customDeal.includedRenders) / 1000))
  );
};

const calculatePlanCost = (user: User, plan: ChargeBeePlan): number => {
  if (plan.legacy) {
    return 0;
  }

  return plan.costInCents + calculateAddonsCost(user, plan);
};

/**
 * Prices a plan the customer does not subscribe to yet at what a switch made on `now`
 * would actually be billed. A candidate has no billing history of its own, so the only
 * date that can decide its generation is the day the new subscription would start; a
 * negotiated custom price never travels to a plan the deal was not struck on.
 */
export const candidatePricedForASwitchOn = (plan: ChargeBeePlan, now: Date): ChargeBeePlan =>
  resolvePlanForSubscription({
    chargebeePlanId: plan.chargeBeePlanId,
    lastCharged: now,
    signupDate: now,
    customPrice: undefined,
  });

export const getSuggestedPlan = (pUser: User, currentPlan: ChargeBeePlan, now: Date = new Date()) => {
  if (currentPlan.legacy || isEnterprise(currentPlan.chargeBeePlanId)) {
    return null;
  }

  // make a dummy estimation for renders
  const user = {
    ...pUser,
    cached_render_counter: Math.max(
      Math.floor(pUser.cached_num_cached * (30 / pUser.environment.cache_freshness)),
      pUser.cached_render_counter
    ),
  };

  const availablePlans = chargeBeePlans.filter(
    (plan) =>
      isPlanAvailable(user, currentPlan.tier, plan) &&
      ![
        ChargeBeePlanId.Basic,
        ChargeBeePlanId.EnterprisePlus,
        ChargeBeePlanId.EnterprisePlusYearly,
        ChargeBeePlanId.CustomEnterprise3Years,
      ].includes(plan.chargeBeePlanId)
  );

  if (user.cached_render_counter < getPlanById(ChargeBeePlanId.Basic).includedRenderedPages) {
    return null;
  }

  // do not suggest free
  let suggestedPlan = isFree(currentPlan.chargeBeePlanId) ? candidatePricedForASwitchOn(availablePlans[0], now) : null;
  let currentPlanCost: number = isFree(currentPlan.chargeBeePlanId)
    ? calculatePlanCost(user, candidatePricedForASwitchOn(availablePlans[0], now))
    : calculatePlanCost(user, currentPlan);

  // Match on the plan id, never on object identity: `resolvePlanForSubscription` hands back a
  // copy of the catalog plan for a repriced subscription, so `indexOf` would answer -1 and the
  // loop would scan plans below the subscriber's own — which the index guard then nulls out.
  const currentPlanIndex = availablePlans.findIndex((plan) => plan.chargeBeePlanId === currentPlan.chargeBeePlanId);

  // we need the find the last plan which cost less than the actual plan + addons
  for (let i = availablePlans.length - 1; i > currentPlanIndex; i--) {
    const p: ChargeBeePlan = candidatePricedForASwitchOn(availablePlans[i], now);
    const planCost = calculatePlanCost(user, p);
    if (planCost < currentPlanCost) {
      suggestedPlan = p;
      currentPlanCost = planCost;
      if (currentPlan.tier !== PlanTier.P2025) {
        break;
      }
    }
  }

  if (
    suggestedPlan &&
    (suggestedPlan.index <= currentPlan.index || suggestedPlan.chargeBeePlanId == ChargeBeePlanId.Lite2024)
  ) {
    suggestedPlan = null;
  }

  return suggestedPlan;
};

const getPlans = (
  user: User,
  currentPlan: ChargeBeePlan,
  suggestedPlan?: ChargeBeePlan,
  now: Date = new Date()
): Array<ChargeBeePlan> => {
  const result = chargeBeePlans
    .filter((plan) => {
      if (user.is_metered_billing) {
        return !plan.legacy;
      } else {
        return true;
      }
    })
    .map((plan) => {
      if (plan.chargeBeePlanId === currentPlan.chargeBeePlanId) {
        // The current-plan row shows the subscription's own resolved figures: the same object
        // `currentPlan` carries, so the list and the overview cannot disagree about one plan.
        return { ...currentPlan, current: true };
      } else if (suggestedPlan && plan.chargeBeePlanId === suggestedPlan.chargeBeePlanId) {
        return { ...candidatePricedForASwitchOn(plan, now), suggested: true };
      } else {
        return { ...candidatePricedForASwitchOn(plan, now) };
      }
    })
    .map((plan) => ({
      ...plan,
      available: isPlanAvailable(user, currentPlan.tier, plan),
    }));

  return result;
};

export const getUserPlans = (user: User, now: Date = new Date()): UserPlans => {
  const currentPlan: ChargeBeePlan = resolvePlanForSubscription({
    chargebeePlanId: user.chargebee_plan_id,
    lastCharged: user.last_charged,
    signupDate: user.signup_date,
    customPrice: user.custom_price,
  });
  const addonCostInCents: number = calculateAddonsCost(user, currentPlan);
  const suggestedPlan = getSuggestedPlan(user, currentPlan, now);
  const plans: Array<ChargeBeePlan> = getPlans(user, currentPlan, suggestedPlan ?? undefined, now)
    .map((p) => ({ ...p }))
    .filter((plan) => plan.available || plan.chargeBeePlanId === currentPlan.chargeBeePlanId);

  return {
    currentPlan,
    addonCostInCents,
    suggestedPlan,
    plans,
  };
};

export function chargeBeePlanIdToPlanName(planId: ChargeBeePlanId): string {
  const planName = PlanIdToNameMap[planId];
  return planName || planId;
}

export function calculateCostToPlanPercentage(billedBy: number, plan: ChargeBeePlan) {
  return Math.round((billedBy / plan.includedRenderedPages) * 100);
}

export function calculateBillingCycleElapsedPercentage(
  lastCharged: Date,
  billingPeriod: BillingPeriod,
  currentTime = new Date()
) {
  const nextCharge = new Date(lastCharged);

  if (billingPeriod === 'yearly') {
    nextCharge.setFullYear(lastCharged.getFullYear() + 1);
  } else {
    nextCharge.setMonth(lastCharged.getMonth() + 1);
  }

  const totalTime = +nextCharge - +lastCharged; // e.g. 30 days
  const elapsedTime = +currentTime - +lastCharged; // e.g. 14 days

  return Math.round((elapsedTime / totalTime) * 100); // e.g. (14 / 30) * 100 = 47%
}

/**
 * What the trial allowance decision needs: the feature flag that picks which plan a trial
 * follows, and the signup date that dates the trial's generation. `signup_date` is optional
 * and nullable for the same reason as `SubscriptionPricingContext.signupDate` — a raw database
 * row can omit the column, and the resolver must never throw on a missing date.
 */
type TrialAllowanceUser = { available_features: string[]; signup_date?: Date | null };

export function getTrialPlanRenderCounterLimit(user: TrialAllowanceUser): number {
  if (user.available_features?.includes('pricing-2025')) {
    // A trial has by definition never been charged, so its signup date is the only
    // billing-period start there is.
    return resolvePlanForSubscription({
      chargebeePlanId: ChargeBeePlanId.Starter,
      signupDate: user.signup_date,
    }).includedRenderedPages;
  } else {
    return getPlanById(ChargeBeePlanId.Essential2024).includedRenderedPages;
  }
}
